import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import axios from 'axios';
import notify from 'devextreme/ui/notify';
import { getApiToken } from '../../utils/authUtils.js';
import { createStore } from 'devextreme-aspnet-data-nojquery';

import './athlete_license_update.scss';

import {
    DataGrid,
    Column,
    Selection,
    Scrolling,
    SearchPanel,
    Toolbar,
    Item,
    HeaderFilter,
    LoadPanel,
    Sorting
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { useNavigate, useSearchParams } from 'react-router-dom';

const cellNameRender = (cell) => (
    <div>{cell.data.lastname}, {cell.data.surname}</div>
);

export const AthleteLicenseUpdate = () => {
    const { instance, accounts } = useMsal();
    const [searchParams] = useSearchParams();
    const competitionId = searchParams.get('competitionId');
    const [gridDataSource, setGridDataSource] = useState();
    const [dataSourceClub, setDataSourceClub] = useState();
    const [dataSourceTrainingGroup, setDataSourceTrainingGroup] = useState();
    const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef(null);
    const navigate = useNavigate();
    const onLeftClick = () => {
        navigate(-1);
    };

    const onRowClick = useCallback(({ data }) => {
        // Nur navigieren, wenn keine Sportler ausgewählt sind
        if (selectedAthleteIds.length === 0) {
            const athleteId = data.athleteId;
            navigate(`/athlete/edit/${athleteId}`);
        }
    }, [navigate, selectedAthleteIds]);

    useEffect(() => {
        getApiToken(instance, accounts).then(token => {
            // Athletes DataSource
            if (competitionId) {
                // Wenn competitionId vorhanden, lade nur Sportler mit Problemen direkt via axios
                // Backend gibt verschachteltes Format zurück: {result_set: [...], success: true}
                axios.get(
                    `${process.env.REACT_APP_serverEndpoint}/Entry/GetAthletesWithLicenseProblems?competitionId=${competitionId}`,
                    {
                        headers: {
                            'Authorization': "Bearer " + token
                        }
                    }
                ).then(response => {
                    if (response.data.success && response.data.result_set) {
                        setGridDataSource(response.data.result_set);
                    } else {
                        setGridDataSource([]);
                    }
                }).catch(error => {
                    console.error('Fehler beim Laden der Sportler:', error);
                    notify({
                        message: 'Fehler beim Laden der Sportler mit Lizenzproblemen',
                        position: { at: 'bottom center', my: 'bottom center' }
                    }, 'error');
                    setGridDataSource([]);
                });
            } else {
                // Standard-Ansicht: Alle Sportler mit abgelaufener/fehlender Lizenz
                setGridDataSource({
                    store: createStore({
                        key: 'athleteId',
                        loadUrl: `${process.env.REACT_APP_serverEndpoint}/Athlete/Athletes`,
                        onBeforeSend: (method, ajaxOptions) => {
                            ajaxOptions.headers = {
                                'Authorization': "Bearer " + token
                            };
                        },
                    }),
                    filter: [['status', '=', 'Aktiv'],'and', [["lisence" ,'<', new Date((new Date()).getFullYear(),11,31)],'or',['lisence','=', null]]] // Nur aktive Athleten
                });
            }

            // Club DataSource
            const clubStore = createStore({
                key: 'clubId',
                loadUrl: `${process.env.REACT_APP_serverEndpoint}/Club/ClubsBasic`,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = {
                        'Authorization': "Bearer " + token
                    };
                },
            });
            setDataSourceClub(clubStore);

            // Training Group DataSource
            const trainingGroupStore = createStore({
                key: 'traininggroupId',
                loadUrl: `${process.env.REACT_APP_serverEndpoint}/Traininggroup/TraininggroupsBasic`,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = {
                        'Authorization': "Bearer " + token
                    };
                },
            });
            setDataSourceTrainingGroup(trainingGroupStore);
        });
    }, [instance, accounts, competitionId]);

    const onSelectionChanged = (e) => {
        setSelectedAthleteIds(e.selectedRowKeys);
    };

    const onUpdateLicense = async () => {
        if (selectedAthleteIds.length === 0) {
            notify({
                message: 'Bitte wählen Sie mindestens einen Sportler aus',
                position: { at: 'bottom center', my: 'bottom center' }
            }, 'warning');
            return;
        }

        setIsLoading(true);

        try {
            const token = await getApiToken(instance, accounts);
            
            await axios.post(
                `${process.env.REACT_APP_serverEndpoint}/Athlete/UpdateLicense`,
               selectedAthleteIds ,
                {
                    headers: {
                        'Authorization': "Bearer " + token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            notify({
                message: `Lizenzverlängerung für ${selectedAthleteIds.length} Sportler erfolgreich`,
                position: { at: 'bottom center', my: 'bottom center' }
            }, 'success');

            // Auswahl zurücksetzen
            setSelectedAthleteIds([]);
            if (gridRef.current) {
                gridRef.current.instance().clearSelection();
            }

            // Grid neu laden
            if (gridRef.current) {
                gridRef.current.instance().refresh();
            }

        } catch (error) {
            console.error('Fehler beim Update der Lizenzen:', error);
            notify({
                message: 'Fehler beim Aktualisieren der Lizenzen',
                position: { at: 'bottom center', my: 'bottom center' }
            }, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const refresh = () => {
        if (gridRef.current) {
            gridRef.current.instance().refresh();
        }
    };

    return (
        <div className='view athlete-license-update'>
            <div className='view-wrapper view-wrapper-athlete-license-update list-page'>
                <DataGrid
                    className='grid theme-dependent'
                    noDataText=''
                    focusedRowEnabled
                    height='100%'
                    dataSource={gridDataSource}
                    keyExpr='athleteId'
                    onSelectionChanged={onSelectionChanged}
                    allowColumnReordering
                    showBorders
                    ref={gridRef}
                    remoteOperations={competitionId ? false : true}
                    onRowClick={onRowClick}
                >
                    <LoadPanel showPane={false} enabled={isLoading} />
                    <SearchPanel visible placeholder='Sportler suchen' />
                    <Selection
                        selectAllMode='allPages'
                        showCheckBoxesMode='always'
                        mode='multiple'
                    />
                    <HeaderFilter visible />
                    <Sorting mode='multiple' />
                    <Scrolling mode='virtual' />
                    
                    <Toolbar>
                         <Item location='before'>
                        <Button icon='arrowleft' stylingMode='text' onClick={onLeftClick} />
                    </Item>
                        <Item location='before'>
                            <div className='grid-header'>
                                {competitionId ? 'Lizenzen aktualisieren - Sportler mit Problemen' : 'Lizenzen aktualisieren'}
                            </div>
                        </Item>
                        <Item location='before'>
                            <div className='info-text'>
                                {selectedAthleteIds.length > 0 
                                    ? `${selectedAthleteIds.length} Sportler ausgewählt`
                                    : ''}
                            </div>
                        </Item>
                        <Item location='after' locateInMenu='auto'>
                            <Button
                                icon='check'
                                text='Lizenzen aktualisieren'
                                type='default'
                                stylingMode='contained'
                                onClick={onUpdateLicense}
                                disabled={selectedAthleteIds.length === 0 || isLoading}
                            />
                        </Item>
                        <Item
                            location='after'
                            locateInMenu='auto'
                            showText='inMenu'
                            widget='dxButton'
                        >
                            <Button
                                icon='refresh'
                                text='Refresh'
                                stylingMode='text'
                                onClick={refresh}
                            />
                        </Item>
                        <Item location='after' locateInMenu='auto'>
                            <div className='separator' />
                        </Item>
                        <Item name='searchPanel' locateInMenu='auto' />
                    </Toolbar>

                  
                    <Column
                        dataField='lastname'
                        caption='Name'
                        sortOrder='asc'
                        hidingPriority={6}
                        minWidth={150}
                        cellRender={cellNameRender}
                        dataType='string'
                    />
                    <Column
                        dataField='surname'
                        caption='Vorname'
                        visible={false}
                        dataType='string'
                    />
                    <Column 
                        dataField='dateofbirth' 
                        caption='Geburtsdatum' 
                        dataType='date' 
                        format='dd.MM.yyyy' 
                        hidingPriority={4}
                        allowHiding={true}
                    />
                    <Column 
                        dataField='lisence' 
                        caption='Lizenz gültig bis' 
                        dataType='date' 
                        format='dd.MM.yyyy' 
                        hidingPriority={5}
                        allowHiding={true}
                    />
                    <Column 
                        dataField='traininggroupId' 
                        caption='Trainingsgruppe' 
                        dataType='string'
                        hidingPriority={2}
                        allowHiding={true}
                        lookup={{ 
                            dataSource: dataSourceTrainingGroup, 
                            valueExpr: 'traininggroupId', 
                            displayExpr: 'groupname' 
                        }}
                    />
                    <Column 
                        dataField='clubId' 
                        caption='Verein' 
                        dataType='string'
                        hidingPriority={3}
                        allowHiding={true}
                        lookup={{ 
                            dataSource: dataSourceClub, 
                            valueExpr: 'clubId', 
                            displayExpr: 'shortclubname' 
                        }}
                    />
                </DataGrid>
            </div>
        </div>
    );
};
